import { FC, useState, useEffect, useMemo } from 'react';
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Box,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  useAcademicsClassesAndCoursesState,
  useAcademicsClassesAndCoursesActions,
} from '../../contexts/academicsClassesAndCourses.context';
import {
  getClassesGridColumns,
  getCoursesGridColumns,
  getSectionSelectOptions,
  getCourseSelectOptions,
  renderSelectMenuItems,
} from './academicsClassesAndCourses.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  EAcademicStream,
  ESubjectType,
} from '../../../../../apis/academics/academicsClassesAndCourses.interfaces';

enum EAssessmentView {
  CLASSES = 'classes',
  COURSES = 'courses',
}

enum EViewType {
  GRID = 'grid',
  DETAILS = 'details',
}

interface AcademicsAssessmentsGridProps {
  isLoading?: boolean;
}

const AcademicsAssessmentsGrid: FC<AcademicsAssessmentsGridProps> = ({
  isLoading: propIsLoading,
}) => {
  const { translateText } = useTranslation();
  const { academicsClassesAndCoursesAppState, isLoading: contextIsLoading } =
    useAcademicsClassesAndCoursesState();
  const { openDetailsMode, closeMode, updatePaginationParams } = useAcademicsClassesAndCoursesActions();

  const [viewType, setViewType] = useState<EAssessmentView>(
    EAssessmentView.CLASSES
  );
  const [currentView, setCurrentView] = useState<EViewType>(EViewType.GRID);
  const [selectedId, setSelectedId] = useState<string | number>('all');

  const classes = academicsClassesAndCoursesAppState.classes;
  const courses = academicsClassesAndCoursesAppState.courses;

  const classesGridColumns = useMemo(
    () => getClassesGridColumns(translateText),
    [translateText]
  );
  const coursesGridColumns = useMemo(
    () => getCoursesGridColumns(translateText),
    [translateText]
  );

  const isLoading = propIsLoading || contextIsLoading;

  // Section select options for classes view
  const sectionOptions = useMemo(
    () => getSectionSelectOptions(classes),
    [classes]
  );

  const courseOptions = useMemo(
    () => getCourseSelectOptions(courses, true, translateText),
    [courses, translateText]
  );

  const selectOptions = useMemo(
    () => (viewType === EAssessmentView.CLASSES ? sectionOptions : courseOptions),
    [viewType, sectionOptions, courseOptions]
  );

  // Reset selection when view type changes
  useEffect(() => {
    setSelectedId('all');
  }, [viewType]);

  // Sync currentView with context state
  useEffect(() => {
    if (
      academicsClassesAndCoursesAppState.selectedClass ||
      academicsClassesAndCoursesAppState.selectedCourse
    ) {
      setCurrentView(EViewType.DETAILS);
    } else {
      setCurrentView(EViewType.GRID);
    }
  }, [
    academicsClassesAndCoursesAppState.selectedClass,
    academicsClassesAndCoursesAppState.selectedCourse,
  ]);

  const handleSelectChange = (event: any) => {
    setSelectedId(event.target.value);
  };

  const handleRowClick = (params: any) => {
    if (viewType === EAssessmentView.CLASSES) {
      const classItem = classes.find((cls) => cls.id === params.row.id);
      if (classItem) {
        openDetailsMode(classItem, 'class');
      }
    } else {
      const courseItem = courses.find((course) => course.id === params.row.id);
      if (courseItem) {
        openDetailsMode(courseItem, 'course');
      }
    }
  };

  const handleBackToGrid = () => {
    closeMode();
    setCurrentView(EViewType.GRID);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    updatePaginationParams({ page, limit: pageSize });
  };

  // Filter rows based on selection
  const filteredRows = useMemo(() => {
    if (selectedId === 'all') {
      return viewType === EAssessmentView.CLASSES ? classes : courses;
    }

    if (viewType === EAssessmentView.CLASSES) {
      return classes.filter((cls) =>
        cls.sections?.some((s) => s.id === selectedId) || cls.section === selectedId
      );
    } else {
      const idNumber = Number(selectedId);
      return courses.filter((course) => course.id === idNumber);
    }
  }, [viewType, selectedId, classes, courses]);

  const selectedClass = academicsClassesAndCoursesAppState.selectedClass;
  const selectedCourse = academicsClassesAndCoursesAppState.selectedCourse;

  const getStreamColor = (stream?: EAcademicStream) => {
    switch (stream) {
      case EAcademicStream.SCIENCE:
        return 'info';
      case EAcademicStream.COMMERCE:
        return 'success';
      case EAcademicStream.ARTS:
        return 'secondary';
      case EAcademicStream.VOCATIONAL:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSubjectTypeColor = (type?: ESubjectType) => {
    switch (type) {
      case ESubjectType.SCIENCE:
        return 'info';
      case ESubjectType.MATHEMATICS:
        return 'secondary';
      case ESubjectType.LANGUAGE:
        return 'success';
      case ESubjectType.COMPUTER:
        return 'warning';
      case ESubjectType.ARTS:
        return 'error';
      default:
        return 'default';
    }
  };

  if (currentView === EViewType.DETAILS) {
    return (
      <Grid
        container
        spacing={0}
        data-testid="academics-classes-courses-details"
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 0 },
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Box display="flex" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 0 }} data-testid="details-header" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGrid}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '16px', sm: '14px' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="back-to-grid-button"
            >
              {viewType === EAssessmentView.CLASSES
                ? translateText('academics.classesAndCourses.backToClasses')
                : translateText('academics.classesAndCourses.backToCourses')}
            </Button>
            <Typography variant="h6" data-testid="detail-header-title" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: 'center', sm: 'left' }, width: { xs: '100%', sm: 'auto' } }}>
              {selectedClass?.name || selectedCourse?.name}
            </Typography>
          </Box>
        </Grid>

        {selectedClass && (
          <Grid item xs={12} data-testid="class-details-container">
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: { xs: 1, sm: 1.5 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  data-testid="class-details-title"
                >
                  {translateText('academics.classesAndCourses.classDetails')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12} sm={6} md={3} data-testid="class-name">
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                      {translateText(
                        'academics.classesAndCourses.columns.className'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {selectedClass.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="class-standard">
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                      {translateText(
                        'academics.classesAndCourses.columns.standard'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {selectedClass.standardLevel || '-'}
                    </Typography>
                  </Grid>
                  {selectedClass.section && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="class-section"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.section'
                        )}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedClass.section}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedClass.stream && (
                    <Grid item xs={12} sm={6} md={3} data-testid="class-stream">
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.stream'
                        )}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedClass.stream}
                          size="small"
                          color={getStreamColor(selectedClass.stream)}
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedClass.board && (
                    <Grid item xs={12} sm={6} md={3} data-testid="class-board">
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.board'
                        )}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedClass.board}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="class-academic-year"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.academicYear'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedClass.academicYear || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="class-tutor">
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.classTeacher'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedClass.classTutor
                        ? `${translateText('academics.classesAndCourses.teacherId')}: ${selectedClass.classTutor}`
                        : translateText(
                          'academics.classesAndCourses.notAssigned'
                        )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="class-strength">
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.students'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedClass.strength || 0}
                        size="small"
                        color={selectedClass.strength ? 'success' : 'default'}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="class-courses">
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.courses'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={`${selectedClass.courseIds?.length || 0} ${(selectedClass.courseIds?.length || 0) === 1
                          ? translateText(
                            'academics.classesAndCourses.course'
                          )
                          : translateText(
                            'academics.classesAndCourses.courses'
                          )
                          }`}
                        size="small"
                        color={
                          (selectedClass.courseIds?.length || 0) > 0
                            ? 'info'
                            : 'default'
                        }
                      />
                    </Box>
                  </Grid>
                  {selectedClass.description && (
                    <Grid item xs={12} data-testid="class-description">
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.description'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedClass.description}
                      </Typography>
                    </Grid>
                  )}
                  {selectedClass.updatedAt && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="class-updated-at"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.lastUpdated'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(selectedClass.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {selectedCourse && (
          <Grid item xs={12} data-testid="course-details-container">
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: { xs: 1, sm: 1.5 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  data-testid="course-details-title"
                >
                  {translateText('academics.classesAndCourses.courseDetails')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  <Grid item xs={12} sm={6} md={3} data-testid="course-name">
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                      {translateText(
                        'academics.classesAndCourses.columns.courseName'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {selectedCourse.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="course-subject-code"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.subjectCode'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedCourse.subjectCode}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="course-subject-type"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.subjectType'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedCourse.subjectType}
                        size="small"
                        color={getSubjectTypeColor(selectedCourse.subjectType)}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="course-teacher">
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.teacher'
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedCourse.teacherId
                        ? `${translateText('academics.classesAndCourses.teacherId')}: ${selectedCourse.teacherId}`
                        : translateText(
                          'academics.classesAndCourses.notAssigned'
                        )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} data-testid="course-classes">
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.assignedClasses'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={`${selectedCourse.classIds.length} ${selectedCourse.classIds.length === 1
                          ? translateText('academics.classesAndCourses.class')
                          : translateText(
                            'academics.classesAndCourses.classes'
                          )
                          }`}
                        size="small"
                        color={
                          selectedCourse.classIds.length > 0
                            ? 'success'
                            : 'default'
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="course-elective"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.elective'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          selectedCourse.isElective
                            ? translateText('attendance.yes')
                            : translateText('attendance.no')
                        }
                        size="small"
                        color={
                          selectedCourse.isElective ? 'warning' : 'default'
                        }
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  {selectedCourse.weightage && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="course-weightage"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.weightage'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCourse.weightage}%
                      </Typography>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    data-testid="course-practicals"
                  >
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'academics.classesAndCourses.columns.practicals'
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          selectedCourse.practicalsIncluded
                            ? translateText('attendance.yes')
                            : translateText('attendance.no')
                        }
                        size="small"
                        color={
                          selectedCourse.practicalsIncluded ? 'info' : 'default'
                        }
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  {selectedCourse.totalLectures && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="course-total-lectures"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.totalLectures'
                        )}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedCourse.totalLectures}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedCourse.syllabus && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="course-syllabus"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.syllabus'
                        )}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={translateText('attendance.yes')}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  )}
                  {selectedCourse.description && (
                    <Grid item xs={12} data-testid="course-description">
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.description'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCourse.description}
                      </Typography>
                    </Grid>
                  )}
                  {selectedCourse.updatedAt && (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      data-testid="course-updated-at"
                    >
                      <Typography variant="caption" color="textSecondary">
                        {translateText(
                          'academics.classesAndCourses.columns.lastUpdated'
                        )}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(
                          selectedCourse.updatedAt
                        ).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <Grid container spacing={0}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 },
          mb: { xs: 1, sm: 2 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
          <ToggleButtonGroup
            size="medium"
            color="primary"
            exclusive
            value={viewType}
            onChange={(_event, value) => {
              value && setViewType(value);
              setSelectedId('all');
            }}
            aria-label="Assessment View"
            sx={{
              height: { xs: 'auto', sm: 'var(--tab-group-height)' },
              '& .MuiToggleButton-root': {
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
              },
            }}
            data-testid="assessment-view-toggle-button-group"
          >
            <Tooltip
              title={translateText(
                'academics.classesAndCourses.classWiseDetails'
              )}
            >
              <ToggleButton
                value={EAssessmentView.CLASSES}
                data-testid="assessment-view-toggle-button-classes"
              >
                {translateText('academics.classesAndCourses.classList')}
              </ToggleButton>
            </Tooltip>
            <Tooltip
              title={translateText(
                'academics.classesAndCourses.subjectWiseCourseDetails'
              )}
            >
              <ToggleButton
                value={EAssessmentView.COURSES}
                data-testid="assessment-view-toggle-button-courses"
              >
                {translateText('academics.classesAndCourses.courseList')}
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ width: { xs: '100%', sm: '250px' } }}>
            <InputLabel sx={{ backgroundColor: 'white', fontSize: { xs: '16px', sm: '14px' } }}>
              {viewType === EAssessmentView.CLASSES
                ? 'Select Section'
                : translateText('academics.classesAndCourses.selectCourse')}
            </InputLabel>
            <Select
              value={selectedId}
              onChange={handleSelectChange}
              label={
                viewType === EAssessmentView.CLASSES
                  ? 'Select Section'
                  : translateText('academics.classesAndCourses.selectCourse')
              }
              sx={{
                minHeight: { xs: '44px', sm: '40px' },
                fontSize: { xs: '16px', sm: '14px' },
              }}
              data-testid="filter-select-dropdown"
            >
              {renderSelectMenuItems(selectOptions)}
            </Select>
            <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {viewType === EAssessmentView.CLASSES
                ? translateText('academics.classesAndCourses.filterByClass')
                : translateText('academics.classesAndCourses.filterByCourse')}
            </FormHelperText>
          </FormControl>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          height: 'calc(100vh - 250px)',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          data-testid="academics-assessments-grid"
          rows={filteredRows}
          columns={
            viewType === EAssessmentView.CLASSES
              ? classesGridColumns
              : coursesGridColumns
          }
          loading={isLoading}
          onRowClick={handleRowClick}
          serverSide={true}
          totalCount={viewType === EAssessmentView.CLASSES ? academicsClassesAndCoursesAppState.totalClasses : academicsClassesAndCoursesAppState.totalCourses}
          currentPage={academicsClassesAndCoursesAppState.paginationParams.page}
          currentPageSize={academicsClassesAndCoursesAppState.paginationParams.limit}
          onPageChange={handlePageChange}
          hideColumnsOnMobile={viewType === EAssessmentView.CLASSES ? ['updatedAt', 'description'] : ['updatedAt', 'description', 'weightage']}
        />
      </Grid>
    </Grid>
  );
};

export default AcademicsAssessmentsGrid;
